import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, SIZES, fmt, formatApiError } from "@/lib/api";
import { Plus, Trash2, ArrowLeft } from "lucide-react";

const blankItem = () => ({ product_id: "", style_id: "", color: "", sizes: Object.fromEntries(SIZES.map((s) => [s, 0])), unit_price: 0 });

export default function ReturnEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [ret, setRet] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/products").then(({ data }) => data),
      api.get(`/returns/${id}`).then(({ data }) => data),
    ]).then(([prods, original]) => {
      setProducts(prods);
      setRet({
        return_date: original.return_date,
        refund_method: original.refund_method || "",
        note: original.note || "",
        items: (original.items || []).map((it) => ({
          product_id: it.product_id,
          style_id: it.style_id,
          color: it.color,
          sizes: { ...Object.fromEntries(SIZES.map((s) => [s, 0])), ...it.sizes },
          unit_price: it.unit_price || 0,
        })),
      });
      setLoading(false);
    }).catch(() => { setErr("Could not load return."); setLoading(false); });
  }, [id]);

  const updateItem = (i, patch) => setRet((p) => ({ ...p, items: p.items.map((it, ix) => ix === i ? { ...it, ...patch } : it) }));
  const updateSize = (i, s, v) => setRet((p) => ({ ...p, items: p.items.map((it, ix) => ix === i ? { ...it, sizes: { ...it.sizes, [s]: Math.max(0, Number(v) || 0) } } : it) }));
  const addItem = () => setRet((p) => ({ ...p, items: [...p.items, blankItem()] }));
  const removeItem = (i) => setRet((p) => ({ ...p, items: p.items.filter((_, ix) => ix !== i) }));

  const onProductChange = (i, productId) => {
    const prod = products.find((x) => x.id === productId);
    if (!prod) { updateItem(i, { product_id: "", style_id: "", color: "", unit_price: 0 }); return; }
    updateItem(i, { product_id: prod.id, style_id: prod.style_id, color: prod.colors[0]?.color || "", unit_price: prod.selling_price || 0 });
  };

  const lineQty = (it) => SIZES.reduce((a, s) => a + (Number(it.sizes[s]) || 0), 0);
  const lineAmount = (it) => lineQty(it) * (Number(it.unit_price) || 0);
  const totalQty = useMemo(() => ret ? ret.items.reduce((a, it) => a + lineQty(it), 0) : 0, [ret]);
  const totalAmount = useMemo(() => ret ? ret.items.reduce((a, it) => a + lineAmount(it), 0) : 0, [ret]);

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = {
        return_date: ret.return_date,
        refund_method: ret.refund_method || "",
        note: (ret.note || "").trim(),
        amount_returned: totalAmount,
        items: ret.items.map((it) => ({
          product_id: it.product_id,
          style_id: it.style_id,
          color: it.color,
          sizes: it.sizes,
          unit_price: Number(it.unit_price) || 0,
        })),
      };
      await api.put(`/returns/${id}`, payload);
      nav(`/returns/${id}`);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  const canSubmit = ret && ret.items.length > 0 && ret.items.every((it) => it.product_id && it.color) && totalQty > 0;

  if (loading) return <div style={{ padding: 40, color: "var(--text-muted)" }}>Loading return...</div>;

  return (
    <div data-testid="return-edit-page">
      <div className="page-header">
        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => nav(`/returns/${id}`)} style={{ marginBottom: 10, paddingLeft: 0 }}><ArrowLeft size={14}/> Cancel Edit</button>
          <h1 className="page-title">Edit Return</h1>
          <p className="page-sub">Adjust returned items — stock is corrected automatically to match the new quantities</p>
        </div>
      </div>

      {err && <div className="error-msg" data-testid="return-edit-error" style={{ marginBottom: 14 }}>{err}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="grid-form">
          <div className="field"><label>Return Date</label><input data-testid="edit-ret-date" className="input" type="date" value={ret.return_date} onChange={(e) => setRet({ ...ret, return_date: e.target.value })}/></div>
        </div>
      </div>

      <div className="card-flush" style={{ marginBottom: 16 }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ fontSize: 16, fontWeight: 600 }}>Returned Items</h2>
          <button className="btn btn-secondary btn-sm" data-testid="edit-add-return-line-btn" onClick={addItem}><Plus size={14}/> Add Item</button>
        </div>
        <div style={{ padding: 16 }}>
          {ret.items.map((it, i) => {
            const prod = products.find((p) => p.id === it.product_id);
            const qty = lineQty(it);
            const amount = lineAmount(it);
            return (
              <div key={i} className="variant-row" data-testid={`edit-ret-line-${i}`} style={{ background: "white" }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr 40px", gap: 10, marginBottom: 10 }}>
                  <div className="field"><label>Product</label>
                    <select data-testid={`edit-ret-line-${i}-product`} className="select" value={it.product_id} onChange={(e) => onProductChange(i, e.target.value)}>
                      <option value="">Select product...</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.style_id} — {p.name}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Color</label>
                    <select data-testid={`edit-ret-line-${i}-color`} className="select" value={it.color} onChange={(e) => updateItem(i, { color: e.target.value })} disabled={!prod}>
                      <option value="">Select...</option>
                      {prod?.colors.map((c) => <option key={c.color} value={c.color}>{c.color}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Est. Return Price (₹/unit)</label><input data-testid={`edit-ret-line-${i}-price`} className="input num" type="number" step="0.01" min="0" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: Number(e.target.value) || 0 })}/></div>
                  <div style={{ display: "flex", alignItems: "flex-end" }}>
                    <button className="btn btn-danger btn-sm" data-testid={`edit-remove-ret-line-${i}`} onClick={() => removeItem(i)} disabled={ret.items.length === 1}><Trash2 size={14}/></button>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${SIZES.length}, 1fr) 90px 120px`, gap: 8, alignItems: "end" }}>
                  {SIZES.map((s) => (
                    <div key={s} className="field">
                      <label>Size {s}</label>
                      <input data-testid={`edit-ret-line-${i}-size-${s}`} className="input num" type="number" min="0" value={it.sizes[s] || 0} onChange={(e) => updateSize(i, s, e.target.value)}/>
                    </div>
                  ))}
                  <div className="field"><label>Qty</label><div style={{ padding: "9px 12px", border: "1px solid var(--border)", borderRadius: 4, background: "#F8FAFC", textAlign: "right" }} className="tabular">{qty}</div></div>
                  <div className="field"><label>Est. Amount</label><div style={{ padding: "9px 12px", border: "1px solid var(--border)", borderRadius: 4, background: "#F8FAFC", textAlign: "right", fontWeight: 600 }} className="tabular">₹ {fmt(amount)}</div></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Refund Details</h2>
        <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 14 }}>Amount to be returned is calculated automatically from the line items above.</p>
        <div className="grid-form">
          <div className="field">
            <label>Amount to be Returned (₹)</label>
            <div data-testid="edit-ret-amount" className="tabular" style={{ padding: "9px 12px", border: "1px solid var(--border)", borderRadius: 4, background: "#F8FAFC", fontWeight: 600, fontSize: 15 }}>₹ {fmt(totalAmount)}</div>
          </div>
          <div className="field"><label>Refund Method</label>
            <select data-testid="edit-ret-refund-method" className="select" value={ret.refund_method} onChange={(e) => setRet({ ...ret, refund_method: e.target.value })}>
              <option value="">— None —</option>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="neft">NEFT</option>
            </select>
          </div>
        </div>
        <div className="field" style={{ marginTop: 14 }}>
          <label>Note (optional)</label>
          <textarea data-testid="edit-ret-note" className="input" rows="2" value={ret.note} onChange={(e) => setRet({ ...ret, note: e.target.value })}/>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button className="btn btn-secondary" onClick={() => nav(`/returns/${id}`)}>Cancel</button>
        <button data-testid="save-return-edit-btn" className="btn btn-primary" disabled={!canSubmit || busy} onClick={submit}>{busy ? "Saving..." : "Save Changes"}</button>
      </div>
    </div>
  );
}
